import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useHousehold, useHouseholds, useGenerateInvite } from '../../hooks/useHousehold';
import { useAuthContext } from '../../contexts/AuthContext';
import { useToast } from './Toast';

interface Props {
  open: boolean;
  onClose: () => void;
}

export function ListSelector({ open, onClose }: Props) {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { data: household } = useHousehold();
  const { data: households = [] } = useHouseholds();
  const { user, signOut, isAdmin, email } = useAuthContext();
  const generateInvite = useGenerateInvite();

  const [showShare, setShowShare] = useState(false);
  const [inviteUrl, setInviteUrl] = useState<string | null>(null);
  const [generatingInvite, setGeneratingInvite] = useState(false);
  const [signingOut, setSigningOut] = useState(false);

  const isPremium = isAdmin || ['PREMIUM', 'COURTESY', 'LIFE_PARTNER'].includes(household?.plan ?? '');
  const isOwner = household?.ownerId === user?.userId;
  const listName = household?.name ?? 'Minha lista';

  const close = () => { onClose(); setShowShare(false); };

  const handleNewList = () => {
    close();
    navigate(isPremium ? '/onboarding' : '/settings/payment');
  };

  const handleShare = () => {
    if (!isPremium) {
      close();
      navigate('/settings/payment');
      return;
    }
    setShowShare(true);
  };

  const handleSettings = () => { close(); navigate('/settings'); };
  const handleAbout = () => { close(); navigate('/about'); };

  const handleSignOut = async () => {
    setSigningOut(true);
    try {
      await signOut();
      navigate('/login');
    } catch {
      addToast('Erro ao sair.', 'error');
      setSigningOut(false);
    }
  };

  const handleGenerateInvite = async () => {
    setGeneratingInvite(true);
    try {
      const result = await generateInvite.mutateAsync();
      setInviteUrl(result.inviteUrl);
      await navigator.clipboard.writeText(result.inviteUrl).catch(() => {});
      addToast('Link de convite copiado!', 'success');
    } catch {
      addToast('Erro ao gerar convite.', 'error');
    } finally {
      setGeneratingInvite(false);
    }
  };

  if (!household || !open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex flex-col justify-end bg-black/40"
      onClick={close}
    >
          <div
            className="bg-white rounded-t-3xl shadow-2xl overflow-hidden"
            style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Handle */}
            <div className="flex justify-center pt-3 pb-2">
              <div className="w-10 h-1 bg-gray-200 rounded-full" />
            </div>

            {!showShare ? (
              <>
                {/* Lists section */}
                <p className="text-xs font-bold text-gray-400 uppercase tracking-wider px-5 pb-2">Listas</p>

                <div className="mx-4 bg-gray-50 rounded-2xl overflow-hidden mb-3">
                  {/* Existing lists */}
                  {(households.length > 0 ? households : [{ id: household.id, name: listName, role: 'OWNER' as const }]).map((h, i, arr) => (
                    <div
                      key={h.id}
                      className={`flex items-center px-4 py-3.5 gap-3 ${i < arr.length - 1 ? 'border-b border-gray-100' : ''}`}
                    >
                      <div className={`w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 ${
                        h.id === household.id ? 'bg-brand-100' : 'bg-gray-200'
                      }`}>
                        <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke={h.id === household.id ? '#059669' : '#9ca3af'} strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                        </svg>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-semibold truncate ${h.id === household.id ? 'text-brand-700' : 'text-gray-700'}`}>
                          {h.name}
                        </p>
                        <p className="text-xs text-gray-400">
                          {h.role === 'OWNER' ? 'Dono' : 'Convidado'}
                        </p>
                      </div>
                      {h.id === household.id && (
                        <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="#059669" strokeWidth={2.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                  ))}

                  {/* New list */}
                  <button
                    onClick={handleNewList}
                    className="w-full flex items-center gap-3 px-4 py-3.5 border-t border-gray-100 touch-manipulation hover:bg-gray-100 transition-colors"
                  >
                    <div className="w-8 h-8 rounded-xl bg-gray-200 flex items-center justify-center flex-shrink-0">
                      <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="#9ca3af" strokeWidth={2.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                      </svg>
                    </div>
                    <span className="text-sm font-semibold text-gray-600 flex-1 text-left">Nova lista</span>
                    {!isPremium && (
                      <span className="flex items-center gap-1 text-xs font-bold text-amber-500 bg-amber-50 px-2 py-0.5 rounded-full">
                        <svg width="10" height="10" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M5 3l3.5 5L12 3l3.5 5L19 3v12H5V3z" />
                        </svg>
                        Premium
                      </span>
                    )}
                  </button>
                </div>

                {/* Actions section */}
                <p className="text-xs font-bold text-gray-400 uppercase tracking-wider px-5 pb-2">Ações</p>

                <div className="mx-4 bg-gray-50 rounded-2xl overflow-hidden mb-4">
                  {/* Share */}
                  <button
                    onClick={handleShare}
                    className="w-full flex items-center gap-3 px-4 py-3.5 border-b border-gray-100 touch-manipulation hover:bg-gray-100 transition-colors"
                  >
                    <div className="w-8 h-8 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0">
                      <svg width="15" height="15" fill="none" viewBox="0 0 24 24" stroke="#2563eb" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                      </svg>
                    </div>
                    <span className="text-sm font-semibold text-gray-700 flex-1 text-left">Compartilhar lista</span>
                    {!isPremium && (
                      <span className="flex items-center gap-1 text-xs font-bold text-amber-500 bg-amber-50 px-2 py-0.5 rounded-full">
                        <svg width="10" height="10" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M5 3l3.5 5L12 3l3.5 5L19 3v12H5V3z" />
                        </svg>
                        Premium
                      </span>
                    )}
                  </button>

                  {/* Settings */}
                  <button
                    onClick={handleSettings}
                    className="w-full flex items-center gap-3 px-4 py-3.5 border-b border-gray-100 touch-manipulation hover:bg-gray-100 transition-colors"
                  >
                    <div className="w-8 h-8 rounded-xl bg-gray-200 flex items-center justify-center flex-shrink-0">
                      <svg width="15" height="15" fill="none" viewBox="0 0 24 24" stroke="#6b7280" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </div>
                    <div className="flex-1 text-left">
                      <p className="text-sm font-semibold text-gray-700">Configurações</p>
                      <p className="text-xs text-gray-400">{email}</p>
                    </div>
                    <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="#d1d5db" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                    </svg>
                  </button>

                  {/* About */}
                  <button
                    onClick={handleAbout}
                    className="w-full flex items-center gap-3 px-4 py-3.5 border-b border-gray-100 touch-manipulation hover:bg-gray-100 transition-colors"
                  >
                    <div className="w-8 h-8 rounded-xl bg-gray-200 flex items-center justify-center flex-shrink-0">
                      <svg width="15" height="15" fill="none" viewBox="0 0 24 24" stroke="#6b7280" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <span className="text-sm font-semibold text-gray-700 flex-1 text-left">Sobre</span>
                  </button>

                  {/* Sign out */}
                  <button
                    onClick={handleSignOut}
                    disabled={signingOut}
                    className="w-full flex items-center gap-3 px-4 py-3.5 touch-manipulation hover:bg-red-50 transition-colors disabled:opacity-50"
                  >
                    <div className="w-8 h-8 rounded-xl bg-red-100 flex items-center justify-center flex-shrink-0">
                      <svg width="15" height="15" fill="none" viewBox="0 0 24 24" stroke="#ef4444" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                      </svg>
                    </div>
                    <span className="text-sm font-semibold text-red-500">
                      {signingOut ? 'Saindo...' : 'Sair da conta'}
                    </span>
                  </button>
                </div>
              </>
            ) : (
              /* Share sub-screen */
              <div className="px-5 pb-6 space-y-4">
                <button
                  onClick={() => setShowShare(false)}
                  className="flex items-center gap-2 text-brand-600 text-sm font-semibold touch-manipulation mb-2"
                >
                  <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                  </svg>
                  Voltar
                </button>

                <p className="text-base font-bold text-gray-900">Compartilhar lista</p>

                {!isOwner ? (
                  <p className="text-sm text-gray-500">Apenas o dono da lista pode convidar membros.</p>
                ) : (
                  <>
                    <p className="text-sm text-gray-600">
                      Convide pessoas para a lista <strong>{listName}</strong>.
                    </p>

                    {inviteUrl && (
                      <div className="bg-gray-50 border border-gray-200 rounded-xl px-3 py-2">
                        <p className="text-xs text-gray-500 break-all">{inviteUrl}</p>
                      </div>
                    )}

                    <button
                      onClick={handleGenerateInvite}
                      disabled={generatingInvite}
                      className="w-full h-12 bg-brand-600 text-white font-semibold rounded-2xl disabled:opacity-50 touch-manipulation"
                    >
                      {generatingInvite ? 'Gerando...' : inviteUrl ? 'Gerar novo link' : 'Gerar link de convite'}
                    </button>

                    {inviteUrl && (
                      <a
                        href={`https://wa.me/?text=${encodeURIComponent('Entre na minha lista do HomeStock: ' + inviteUrl)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-center gap-2 w-full h-12 bg-green-500 text-white font-semibold rounded-2xl text-sm touch-manipulation"
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="white">
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                        </svg>
                        Compartilhar via WhatsApp
                      </a>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
    </div>
  );
}
